import classNames from 'classnames';
import React from 'react';
import { HEADER, BUTTONS_TEXT, POPUP_NAMES } from '../../constants/popups';
import { localizeDate } from '../localization';
import MeasureSelectionDropdownItem from './MeasureSelectionDropdownItem';
import MeasureSelectionDropdownLegendItem from './MeasureSelectionDropdownLegendItem';
import { MEASURE_NAMES } from '../../constants/assessments';
import { BM_PERIODS } from '../../constants/enrollment';

const getAssessorName = ({ officialResultAssessorName }) =>
    officialResultAssessorName || 'Someone else';

export const getPopupContent = (name, params) => {
    switch (name) {
        case POPUP_NAMES.DISCONTINUED:
        case POPUP_NAMES.TESTED_OUT: {
            return {
                header: HEADER.NOT_REQUIRED,
                message: (
                    <span>
                        {`This measure has been ${name} and `}
                        <span className='dashboard-bold'>is not</span> required for this student. If you
                        assess this measure, the results will be used in composite score
                        calculation.
                    </span>
                ),
                cancelButtonText: BUTTONS_TEXT.CANCEL,
                continueButtonText: BUTTONS_TEXT.CONTINUE,
            };
        }
        case POPUP_NAMES.GRADE_NOT_AVAILABLE: {
            return {
                header: HEADER.GRADE_NOT_AVAILABLE,
                message: <span>mCLASS DIBELS 8 is not available for this grade level.</span>,
                okButtonText: BUTTONS_TEXT.OK,
            };
        }
        case POPUP_NAMES.GRADE_NOT_AVAILABLE_SPANISH: {
            return {
                header: HEADER.GRADE_NOT_AVAILABLE,
                message: <span>Assessment not available for this grade level.</span>,
                okButtonText: BUTTONS_TEXT.OK,
            };
        }
        case POPUP_NAMES.MAZE_SCORE: {
            return {
                header: HEADER.MAZE_SCORE,
                message: (
                    <span>
                        Item-level details are not available for this measure. Please review the
                        results in the web reports.
                    </span>
                ),
                okButtonText: BUTTONS_TEXT.OK,
            };
        }
        case POPUP_NAMES.CP_SCORE: {
            return {
                header: HEADER.CP_SCORE,
                message: (
                    <span>
                        Item-level details are not available for this measure. Please review the
                        results in the web reports.
                    </span>
                ),
                okButtonText: BUTTONS_TEXT.OK,
            };
        }
        case POPUP_NAMES.AI_NO_DETAILS_AVAILABLE_POPUP: {
            return {
                header: HEADER.NO_DETAILS_AVAILABLE,
                message: <span>This result was added using Assessment Importer.</span>,
                okButtonText: BUTTONS_TEXT.OK,
            };
        }
        case POPUP_NAMES.LECTURA_NO_DETAILS_AVAILABLE_POPUP: {
            return {
                header: HEADER.NO_DETAILS_AVAILABLE,
                message: (
                    <span>No details are available because this result was added manually.</span>
                ),
                okButtonText: BUTTONS_TEXT.OK,
            };
        }
        case POPUP_NAMES.OPTIONAL_ASSESS: {
            return {
                header: HEADER.CAUTION,
                message: (
                    <span>
                        You should administer the vocabulary measure only if all intervention
                        students will receive it. Administering the assessment to some, but not all,
                        intervention students may prevent lessons from being as targeted as
                        possible.
                    </span>
                ),
                cancelButtonText: BUTTONS_TEXT.CANCEL,
                continueButtonText: BUTTONS_TEXT.GO_TO_MEASURE,
            };
        }
        case POPUP_NAMES.NO_RESULT_DETAILS_POPUP: {
            return {
                header: HEADER.NO_DETAILS_AVAILABLE,
                message: (
                    <span>
                        {`${getAssessorName(params)} assessed ${params.studentName} `}
                        with {`${params.measure} probe on `}
                        {localizeDate(params.officialResultDate)}. Item level details are not
                        available.
                    </span>
                ),
                okButtonText: BUTTONS_TEXT.OK,
            };
        }
        case POPUP_NAMES.UNOFFICIAL_RESULT_POPUP: {
            return {
                header: HEADER.REVIEW_UNOFFICIAL_RESULTS,
                message: (
                    <span>
                        There is more than one {params.navigationDetails.period} result
                        {` for ${params.measure}`}.
                        {` ${getAssessorName(params)} administered the official result `}
                        on {localizeDate(params.officialResultDate)}; you administered the
                        unofficial result on
                        {` ${localizeDate(params.currentStaffResultDate)}`}.
                    </span>
                ),
                cancelButtonText: BUTTONS_TEXT.CANCEL,
                reviewButtonText: BUTTONS_TEXT.REVIEW,
            };
        }
        case POPUP_NAMES.NO_DETAILS_AVAILABLE_POPUP: {
            return {
                header: HEADER.NO_DETAILS_AVAILABLE,
                message: (
                    <span>
                        Details for this form are no longer available. They can be viewed in the
                        mCLASS student reports.
                    </span>
                ),
                okButtonText: BUTTONS_TEXT.OK,
            };
        }
        case POPUP_NAMES.TRC_OUTSIDE_BM_POPUP: {
            return {
                header: HEADER.NO_DETAILS_AVAILABLE,
                okButtonText: BUTTONS_TEXT.OK,
            };
        }
        case POPUP_NAMES.G5_ORF_PM_FORM1_ALT_TEXT_POPUP: {
            return {
                header: HEADER.G5_ORF_PM1_ALT_TEXT,
                message: (
                    <>
                        <p>
                            There are two available versions of this Grade 5 ORF PM text. Before
                            assessing, please confirm which version your student is reading.
                        </p>
                        <ul>
                            <li>
                                <p>
                                    If you are using a printed kit purchased directly from Amplify,
                                    tap <b>Printed Kit</b>.
                                </p>
                            </li>
                            <li>
                                <p>
                                    If you are using a form you downloaded from Amplify or from the
                                    University of Oregon, tap <b>Downloaded PDF</b>.
                                </p>
                            </li>
                        </ul>
                    </>
                ),
                printedKitButtonText: BUTTONS_TEXT.PRINTED_KIT,
                downloadedPDFButtonText: BUTTONS_TEXT.DOWNLOADED_PDF,
                cancelButtonText: BUTTONS_TEXT.CANCEL,
            };
        }
        case POPUP_NAMES.NO_IDEL_SUPPORT_POPUP: {
            return {
                header: HEADER.NOTIFICATION,
                message: (
                    <span>
                        Amplify has ended its support for mCLASS IDEL. <br />
                        For additional assistance, please contact Amplify Customer Care and Support.
                    </span>
                ),
                okButtonText: BUTTONS_TEXT.OK,
            };
        }
        default:
            return {
                header: '',
                message: '',
            };
    }
};

export const isDigitalFormByMeasureEnabled = (index, measure) =>
    index >= 0 && measure === MEASURE_NAMES.FLO;

export const isDigitalFormsPopupEnabled = (formSequence, measureName, isPopupEnabled) =>
    isDigitalFormByMeasureEnabled(formSequence, measureName) && isPopupEnabled;

export const getFormOptions = dropdownItemData => {
    return dropdownItemData.map(data => ({
        value: data.formUid,
        label: (
            <MeasureSelectionDropdownItem
                key={data.formUid}
                label={data.formName}
                numOfUsedForms={data.timesUsedInResults}
                genre={data.genre}
            />
        ),
    }));
};

export const formLegendOption = {
    label: <MeasureSelectionDropdownLegendItem />,
    className: classNames('dashboard-option-disabled'),
};

export const getPreviousPeriod = period => BM_PERIODS[BM_PERIODS.indexOf(period) - 1];

export const getFocusableElementsList = modalWindow => {
    const focusableElements = modalWindow.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    return [firstElement, lastElement, focusableElements];
};

export const handleTabKeyPress = (firstFocusElement, lastFocusElement) => e => {
    if (e.key !== 'Tab') return;

    if (e.shiftKey && document.activeElement === firstFocusElement) {
        e.preventDefault();
        lastFocusElement.focus();
    } else if (!e.shiftKey && document.activeElement === lastFocusElement) {
        e.preventDefault();
        firstFocusElement.focus();
    }
};

export const handleEnterKeyPress = e => {
    if (e.key === 'Enter') {
        e.target.click();
    }
};
